import { useEffect, useState, useRef, useMemo } from "react";

import { AgGridReact } from "ag-grid-react";
import { Box, Autocomplete, TextField, FormControl, FormControlLabel, Radio, RadioGroup } from "@mui/material";
import { lightBlue } from "@mui/material/colors";
import { useDispatch } from "react-redux";
import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { SplitPane } from "react-collapse-pane";

import itemService from "@plannel/services/data/item-service";
import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import AttributeConfigModal from "@plannel/components/modal/AttributeConfigModal";
import ItemAutocomplete from "@plannel/components/filter/ItemAutocomplete";
import PaginationContainer from "@plannel/components/PaginationContainer"
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import reduxUtil from "@plannel/utils/redux-util";
import dateUtils from "@plannel/utils/date-util";
import bomVersionUtils from "@plannel/utils/bom-version-util";
import debug from "@plannel/utils/debug-util";
import colorUtil from "../utils/color-util";
import codeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import bomService from "@plannel/services/data/bom-service";
import bomVersionService from "@plannel/services/data/bom-version-service";
import CustomDetailHeader from "@plannel/components/aggrid/CustomDetailHeader";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import { FilterButton } from "@plannel/components/ActionIconButton";

const { itemType, hrchyNum } = codeUtil;
const { snackBarMessageColor } = colorUtil;

const BASE_BOM_VER_ID = 0;
const BOMDetail = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const pageSizes = [100, 200, 500];
  const [currentPage, setCurrentPage] = useState(reduxViewState ? reduxViewState.currentPage : 1);
  const [tPages, setTotalPages] = useState(0);
  const [pageSize, setPageSize] = useState(reduxViewState ? reduxViewState.pageSize : pageSizes[0]);

  const [sortParams, setSortParams] = useState({});
  const itemSearch = useRef(reduxViewState?.itemSearch ? reduxViewState.itemSearch : "");
  const extraItemCodesRef = useRef(reduxViewState?.extraItemCodes ?? []);

  const itemTypes = Object.keys(itemType);
  const [itemTypeSearch, setItemTypeSearch] = useState(reduxViewState?.itemTypeSearch ?? null);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const itemGridRef = useRef();
  const BomDetailGridRef = useRef();
  const [gridLoading, setGridLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [itemHierarchies, setItemHierarchies] = useState({});
  const [bomDetail, setBomDetail] = useState([]);
  const [selectedRow, setSelectedRow] = useState(reduxViewState?.selectedRow ? reduxViewState?.selectedRow : null);
  const expansionTypes = ["DOWN", "UP"];
  const [expansionType, setExpansionType] = useState(reduxViewState?.expansionType ? reduxViewState?.expansionType : expansionTypes[0]);
  /* 원자재는 하위 구성이 없어 정전개할 대상이 없다 — 역전개만 쓴다. */
  const isRawMaterial = selectedRow?.itemType === itemType.RM.cd;
  /* 전개 방향이 품목 선택 때문에 바뀐 경우 표시. 조회는 품목 선택 이펙트가 맡으므로
     전개 방향 이펙트에서 같은 조회를 한 번 더 하지 않게 한다. */
  const expansionTypeBySelectionRef = useRef(false);
  /* 선택한 품목의 BOM 버전 드롭다운 옵션과 선택값. 옵션은 { id, bomVerCd } 이고, 기준 BOM 은
     id 0 · bomVerCd 없음으로 표현한다. */
  const [bomVersions, setBomVersions] = useState([]);
  const [bomVersion, setBomVersion] = useState(null);
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const [attrConfigModalOpen, setAttrConfigModalOpen] = useState(false);
  const [attrColDefs, setAttrColDefs] = useState([]);
  const [hrchyColDefs, setHrchyColDefs] = useState([]);
  const editableColor = lightBlue[100];
  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusBar = useMemo(() => {
    return {
      statusPanels: [
        { statusPanel: "agTotalRowCountComponent", align: "center" },
        { statusPanel: "agAggregationComponent" }
      ]
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const attrFields = ["attr01", "attr02", "attr03", "attr04", "attr05", "attr06", "attr07", "attr08", "attr09", "attr10"];
  const attrColsCollapsed = reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "attrColsCollapsed") ? reduxViewState.attrColsCollapsed : true;
  const [hrchyColsCollapsed, setHrchyColsCollapsed] = useState(() => reduxViewState && Object.prototype.hasOwnProperty.call(reduxViewState, "hrchyColsCollapsed") ? reduxViewState.hrchyColsCollapsed : true);
  const hrchyNumList = {
    itemHrchy: hrchyNum.HRCHY,
    itemHrchy02: hrchyNum.HRCHY02,
    itemHrchy03: hrchyNum.HRCHY03,
    itemHrchy04: hrchyNum.HRCHY04,
    itemHrchy05: hrchyNum.HRCHY05
  }
  const getRequestParams = (isPagination = true) => {
    const params = {};
    const searchFilters = {};

    if (extraItemCodesRef.current?.length > 0) {
      searchFilters.items = [itemSearch.current, ...extraItemCodesRef.current].filter(Boolean);
    } else if (itemSearch.current) {
      searchFilters.item = itemSearch.current;
    }

    if (itemTypeSearch) {
      searchFilters.itemType = itemTypeSearch;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters
    }

    if (isPagination) {
      if (currentPage) {
        params.page = currentPage - 1;
      }

      if (pageSize) {
        params.pageSize = pageSize;
      }
    }
    params.searchFilters = searchFilters;

    return { ...params, ...sortParams };
  }

  const retrieveItems = () => {
    const params = getRequestParams();
    itemService.getAll(params)
      .then(response => {
        const { results, totalPages } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        setItems(results);
        setTotalPages(totalPages);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { itemSearch: itemSearch.current, extraItemCodes: extraItemCodesRef.current, itemTypeSearch, currentPage, pageSize, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(itemGridRef.current?.api);

        if (selectedRow && itemGridRef.current?.api) {
          setTimeout(() => {
            itemGridRef.current.api.forEachNode(node => {
              if (node.data?.itemCd === selectedRow.itemCd) {
                node.setSelected(true);
                itemGridRef.current.api.ensureNodeVisible(node);
              }
            });
          }, 0);
        }
      });
  };

  const effStartTime = version => {
    const time = version?.effStartDt ? new Date(version.effStartDt).getTime() : 0;

    return isNaN(time) ? 0 : time;
  };

  /* BOM 버전 드롭다운 옵션. 기준 BOM 행이 있으면 맨 위에 두고, 버전은 소유 완제품 → 유효 기간(시작일)
     순으로 정렬한다 — 기준 BOM 은 버전의 유효 기간이 적용되지 않는 기간의 BOM 이라 유효 기간이 없다.
     id 는 BIGINT 라 Number 로 바꾸면 값이 깨지므로 문자열로 들고 다닌다. bomVerCd 가 없는 옵션이
     기준 BOM 이고, 표시 문구는 언어 변경을 따라가도록 렌더 시점에 붙인다.
     itemCd 는 버전을 소유한 완제품이다 — 반제품은 상위 완제품의 버전으로 전개되므로 후보가
     (완제품 × 버전) 으로 여러 건 나오고, 버전 코드는 완제품별로만 고유해서(z_bom_version_uk)
     소유 완제품이 없으면 서로 구분되지 않는다. 완제품 순으로 정렬해 같은 완제품의 버전이 붙어 보인다. */
  const toBomVersionOptions = (versions, baseBomRowCnt) => {
    const options = [...versions]
      .sort((left, right) => String(left.itemCd ?? "").localeCompare(String(right.itemCd ?? ""))
        || effStartTime(left) - effStartTime(right)
        || String(left.bomVerCd ?? "").localeCompare(String(right.bomVerCd ?? "")))
      .map(version => ({ id: String(version.id), bomVerCd: version.bomVerCd, itemCd: version.itemCd }));

    if (baseBomRowCnt > 0) {
      options.unshift({ id: String(BASE_BOM_VER_ID), bomVerCd: null, itemCd: null });
    }

    return options;
  };

  /* 버전 소유 완제품이 전개 기준 품목과 다를 때(= 반제품을 상위 완제품의 버전으로 전개할 때)만
     완제품 코드를 앞에 붙인다 (bomVersionUtils.bomVersionLabel). */
  const bomVersionLabel = option => {
    if (!option) {
      return "";
    }

    if (!option.bomVerCd) {
      return t("baseBom");
    }

    return bomVersionUtils.bomVersionLabel(option.bomVerCd, option.itemCd, selectedRow?.itemCd);
  };

  /* 전개에 적용할 수 있는 버전. 반제품은 자기 버전을 가질 수 없지만 상위 완제품 버전의 z_bom 행에
     자기 구성이 들어 있어 그 버전으로 전개되므로, 품목 유형으로 가르지 않고 서버가 후보를 정한다.
     원자재는 하위 구성이 없어 정전개 대상이 아니라 조회하지 않는다. */
  const loadBomVersions = item => {
    if (!item?.itemCd || item.itemType === itemType.RM.cd) {
      setBomVersions([]);
      return Promise.resolve([]);
    }

    return bomVersionService.getApplicable({ searchFilters: { itemCd: item.itemCd } })
      .then(response => {
        const options = toBomVersionOptions(response.data?.results ?? [], response.data?.baseBomRowCnt ?? 0);
        setBomVersions(options);
        return options;
      })
      .catch(e => {
        debug(e);
        setBomVersions([]);
        return [];
      });
  };

  /* 정전개에 적용할 bom_ver_id.
     · 고른 옵션의 id 를 그대로 쓴다. 기준 BOM 은 0 이고, 버전을 고르면 그 버전 행만(품목 단위)
       전개된다 — 기준 BOM 은 버전의 유효 기간이 적용되지 않는 기간의 BOM 이라 함께 전개되지 않는다.
     · 아무것도 고르지 않았으면 첫 옵션(기준 BOM 이 있으면 기준 BOM)을 쓴다.
     · 옵션이 아예 없으면 조건을 걸지 않는다(null). 옵션은 기준 BOM 행 또는 이 품목이 참여하는 버전이
       있으면 생기므로, 옵션이 없다는 것은 이 품목에 구성 행이 아무 데도 없다는 뜻이다 — 어느 쪽으로
       전개해도 결과가 루트 한 행이라 조건 유무가 결과를 바꾸지 않는다. */
  const getBomVerId = (version, options) => {
    if (version?.id) {
      return version.id;
    }

    return isEmpty(options) ? null : options[0].id;
  };

  /* 전개 방향은 인자로 받는다 — 품목을 바꿀 때 방향과 조회를 같은 흐름에서 처리해야 해서,
     setExpansionType 이 반영되기 전 상태 값을 그대로 쓰면 이전 방향으로 조회된다. */
  const retrieveBomDetail = (selectedRow, version = bomVersion, options = bomVersions, expansion = expansionType) => {
    if (!selectedRow) {
      setBomDetail([]);
      return;
    }

    const params = {}
    params.expansionType = expansion;
    params.itemCd = selectedRow?.itemCd;
    params.itemType = selectedRow?.itemType;

    /* 버전은 상위 품목(완제품·판매 키트)의 정전개 기준으로 정의되므로 역전개에는 적용하지 않는다.
       역전개는 이 품목을 쓰는 모든 상위 품목을 찾는 것이라, 특정 버전으로 자르면 결과가 누락된다. */
    if (expansion === expansionTypes[0]) {
      const bomVerId = getBomVerId(version, options);

      if (bomVerId !== null) {
        params.bomVerId = bomVerId;
      }
    }

    bomService.getBomDetail(params)
      .then(response => {
        const { data } = response?.data;
        if (isEmpty(data)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }
        setBomDetail(data);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { selectedRow, expansionType: expansion });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(BomDetailGridRef.current?.api);
      });
  };

  const splitListByHrchyNum = (list) => {
    const result = {};
    list.forEach(item => {
      const num = item.hrchyNum;
      if (!Object.prototype.hasOwnProperty.call(result, num)) {
        result[num] = [];
      }
      result[num].push(item);
    });
    return result;
  }

  const retrieveItemHierarchies = () => {
    itemHrchyService.getLookup()
      .then(response => {
        const result = response.data.results;
        setItemHierarchies(splitListByHrchyNum(result));
      })
      .catch(e => {
        debug(e);
      });
  }

  const onItemGridReady = () => {
    retrieveItemHierarchies();
    setGridLoading(true);
  }

  const onItemSelected = params => {
    const selectedNode = params.api?.getSelectedRows();
    if (!selectedNode.length) {
      setSelectedRow(null);
      return;
    }
    setSelectedRow(selectedNode[0]);
  }

  const initPageToRetrieve = () => {
    if (currentPage > 1) {
      setCurrentPage(1);
    } else {
      retrieveItems();
      retrieveBomDetail();
    }
  }

  const itemValueFormatter = params => {
    const { value } = params;

    return itemType?.[value] ? GridUtils.gridValueL10N(itemType[value]?.name) : value;
  };

  const itemHrchyValueFormatter = (itemHrchy) => {
    return itemHrchy.value ? itemHrchy.value.name : "";
  };

  const itemHrchyKeyCreator = (params) => {
    return params.value.hrchyCd;
  }

  const getItemHrchyList = (num) => {
    if (itemHierarchies === null) {
      return [];
    }
    if (Object.prototype.hasOwnProperty.call(itemHierarchies, num)) {
      return itemHierarchies[num];
    }
    return [];
  };

  const itemColDefs = [
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 150, cellClass: 'stringType', filterType: "string" },
    { headerName: "itemName", field: "name", type: ["nonEditableColumn"], cellClass: 'stringType', width: 150, filterType: "string" },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], width: 150, valueFormatter: itemValueFormatter, filterType: "string", filterOption: Object.keys(itemType).map(key => key.toLowerCase()) },
    { headerName: "uomCd", field: "uomCd", type: ["nonEditableColumn"], width: 70, filterType: "string" },
    { headerName: "descTxt", field: "descTxt", type: ["nonEditableColumn"], width: 200, filterType: "string" },
  ];

  /* 행이 속한 (전개 기준 품목 × BOM 버전) 의 버전. 버전은 완제품·판매 키트에 소속되므로 정전개는
     트리 전체가, 역전개는 경로마다 같은 값을 갖는다. bomVerId 가 없으면 버전을 가리지 않는 조회라
     표시할 버전이 없다 — 비워 둔다.
     반제품을 상위 완제품의 버전으로 전개하면 소유 완제품이 트리 어디에도 나오지 않고, 버전 코드는
     완제품별로만 고유하므로 소유 완제품(bomVerItemCd)을 앞에 붙여 어느 완제품의 버전인지 밝힌다.
     전개 기준 품목은 hierarchy[0] 에서 읽는다 — 이 컬럼 정의는 onDetailGridReady 에서 한 번만
     적용되므로 selectedRow 를 클로저로 잡으면 첫 렌더 값에 고정된다. */
  const bomVersionValueFormatter = params => {
    if (params.data?.bomVerId == null) {
      return "";
    }

    if (!params.value) {
      return t("baseBom");
    }

    return bomVersionUtils.bomVersionLabel(params.value, params.data?.bomVerItemCd, params.data?.hierarchy?.[0]);
  };

  /* 그 버전의 유효 기간. 기준 BOM(bomVerId = 0)은 기간이 없어 비어 있다. */
  const effPeriodValueGetter = params => {
    const { effStartDt, effEndDt } = params.data ?? {};
    if (!effStartDt && !effEndDt) {
      return "";
    }

    return `${dateUtils.formatDate(effStartDt, "yyyy-MM-dd")} ~ ${dateUtils.formatDate(effEndDt, "yyyy-MM-dd")}`;
  };

  const bomDetailColDefs = [
    { headerName: "itemCd", field: "itemCd", type: ["nonEditableColumn"], width: 150, sortable: false },
    { headerName: "itemName", field: "itemName", type: ["nonEditableColumn"], width: 150, sortable: false },
    { headerName: "itemType", field: "itemType", type: ["nonEditableColumn"], valueFormatter: itemValueFormatter, width: 150, sortable: false },
    { headerName: "bomVersion", field: "bomVerCd", type: ["nonEditableColumn"], width: 120, sortable: false, valueFormatter: bomVersionValueFormatter },
    { headerName: "effPeriod", colId: "effPeriod", type: ["nonEditableColumn"], width: 180, sortable: false, valueGetter: effPeriodValueGetter },
    { headerName: "uomCd", field: "uomCd", type: ["nonEditableColumn"], width: 150, sortable: false },
    { headerName: "bomRate", field: "consumptionRate", type: ["decimalPointColumn", "nonEditableColumn", "rightAligned"], width: 150, sortable: false },
    {
      headerName: "calculationQty", field: 'consumptionQty', type: ["decimalPointColumn", "rightAligned"], width: 150, sortable: false, editable: params => params.data?.level === 0,
      cellStyle: params => params.data?.level === 0 ? { backgroundColor: editableColor } : {},
      valueParser: params => {
        const newValue = parseFloat(params.newValue);
        if (isNaN(newValue) || newValue <= 0) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterNumberGreaterThanValue", { N: 0 }) });
          return params.oldValue;
        }
        return newValue;
      },
    },
  ];

  const onDetailGridReady = params => {
    setGridLoading(true);
    GridUtils.setColumnDefs({ ...params, columnDefs: bomDetailColDefs, viewName, gridId: "bomDetailGrid", initState: true });
  }

  const onCellValueChanged = params => {
    if (params.data.level === 0 && params.colDef.field === "consumptionQty") {
      const field = params.colDef.field
      const oldQty = parseFloat(params.oldValue);
      const newQty = parseFloat(params.newValue);
      const ratio = newQty / oldQty;
      const changedNode = params.node;
      const descendants = [];

      const collectDescendants = node => {
        node.childrenAfterGroup?.forEach((child) => {
          if (child.data?.[field] !== null) {
            child.setDataValue(field, (child.data?.[field] * ratio));
          }
          descendants.push(child);
          collectDescendants(child);
        });
      };

      collectDescendants(changedNode);

      BomDetailGridRef?.api?.refreshCells({
        rowNodes: descendants,
        columns: [field],
        force: true,
      });
    }
  };

  /* 역전개 경로의 최상위(완제품·판매 키트) 행을 강조한다 — 같은 완제품이 버전 때문에 여러 번
     나오므로, 경로가 어디서 끝나는지 보이지 않으면 어느 행까지가 한 경로인지 읽기 어렵다. */
  const getRowStyle = params => {
    if (params.data?.topLevel) {
      return { background: lightBlue[50], boxShadow: `inset 3px 0 0 ${lightBlue[800]}` };
    }

    return DataState.getRowStyles(params);
  };

  const handleChange = (event) => {
    const value = event?.target?.value;
    setExpansionType(value);
  };

  const changeBomVersion = (_event, newValue) => {
    setBomVersion(newValue);
    retrieveBomDetail(selectedRow, newValue);
  };

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };


  useEffect(() => {
    if (gridLoading) {
      DataState.initialize(itemGridRef.current?.api);
      retrieveItems();
      retrieveBomDetail();
    }

    return () => {
      setItems([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, gridLoading, sortParams]);

  useEffect(() => {
    if (gridLoading) {
      initPageToRetrieve();
    }

    return () => {
      setItems([]);
      setBomDetail([]);
      setSelectedRow(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters, itemTypeSearch]);

  useEffect(() => {
    if (Object.keys(hrchyColDefs).length > 0 && !isEmpty(attrColDefs)) {

      const parentDetailColumns = hrchyColDefs.map(col => col.field);
      parentDetailColumns.shift();

      attrColDefs.forEach((colDef) => {
        colDef.type = ['nonEditableColumn'];
        colDef.filterType = "string"
        colDef.filterName = colDef.field;
      });

      hrchyColDefs.forEach((colDef, index) => {
        colDef.type = ['nonEditableColumn'];
        colDef.width = 170;
        colDef.filterType = "string";
        colDef.filterOption = getItemHrchyList(hrchyNumList[colDef.field]).map(item => item.name);
        colDef.cellEditorParams = {
          valueFormatter: itemHrchyValueFormatter,
          values: getItemHrchyList(hrchyNumList[colDef.field])
        };

        if (index === 0) {
          colDef.headerComponent = CustomDetailHeader;
          colDef.headerComponentParams = {
            viewName,
            detailColumns: parentDetailColumns,
            colsHide: hrchyColsCollapsed,
            reduxKey: "hrchyColsCollapsed",
            onToggleCollapse: (newValue) => {
              setHrchyColsCollapsed(newValue);
              const reduxState = reduxUtil.createObjectState(viewName, { hrchyColsCollapsed: newValue });
              reduxDispatch(reduxUtil.updateViewState(reduxState));
            }
          };
        } else {
          colDef.hide = hrchyColsCollapsed;
        }
      });

      const finalColumnDefs = [...itemColDefs, ...hrchyColDefs, ...attrColDefs]
      GridUtils.setColumnDefs({ ...itemGridRef.current, columnDefs: finalColumnDefs, viewName, initState: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrchyColDefs, attrColDefs, itemHierarchies]);

  /* 품목을 고르면 그 품목의 BOM 버전 정보를 다시 읽어 드롭다운에 채운다. 선택값은 첫 옵션
     (기준 BOM 이 있으면 기준 BOM, 없으면 정렬 첫 버전)으로 맞춰, 드롭다운에 보이는 데이터 세트와
     전개 결과가 어긋나지 않게 한다.
     전개 방향도 품목에 맞춰 되돌린다 — 원자재는 역전개, 그 외 품목은 정전개. 이전 품목에서 고른
     방향이 남아 다른 품목에 그대로 적용되지 않게 한다. */
  useEffect(() => {
    setBomVersion(null);

    if (!selectedRow) {
      loadBomVersions(null);
      return;
    }

    const nextExpansionType = isRawMaterial ? expansionTypes[1] : expansionTypes[0];

    if (nextExpansionType !== expansionType) {
      expansionTypeBySelectionRef.current = true;
      setExpansionType(nextExpansionType);
    }

    loadBomVersions(selectedRow).then(options => {
      /* 역전개에는 버전을 적용하지 않으므로 드롭다운 선택값을 비워 둔다. */
      const defaultOption = nextExpansionType === expansionTypes[0] ? (options[0] ?? null) : null;

      setBomVersion(defaultOption);
      retrieveBomDetail(selectedRow, defaultOption, options, nextExpansionType);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRow]);

  useEffect(() => {
    if (expansionTypeBySelectionRef.current) {
      expansionTypeBySelectionRef.current = false;
      return;
    }

    if (selectedRow) {
      retrieveBomDetail(selectedRow);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expansionType]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
        <ItemAutocomplete retrieve={initPageToRetrieve} searchValue={itemSearch} useAccessFilter={false} extraCodesRef={extraItemCodesRef} />
        <Autocomplete
          options={itemTypes}
          getOptionLabel={option => itemType ? t(itemType[option]?.name) : option}
          value={itemTypeSearch}
          onChange={(_, newValue) => setItemTypeSearch(newValue)}
          isOptionEqualToValue={(option, value) => option === value}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("itemType")} />
          )}
        />
        {/* 전개할 BOM 버전은 다른 옵션으로 바꿀 수만 있고 비울 수는 없다(disableClearable) —
            비우면 첫 옵션(기준 BOM)으로 전개되면서 드롭다운에 보이는 값과 전개 결과가 어긋난다. */}
        <Autocomplete
          options={bomVersions}
          value={bomVersion}
          onChange={changeBomVersion}
          getOptionLabel={bomVersionLabel}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          disableClearable
          disabled={isEmpty(bomVersions) || expansionType !== expansionTypes[0]}
          size="small"
          sx={{ mr: 1, minWidth: 200 }}
          renderInput={(params) => (
            <TextField {...params} label={t("bomVersion")} />
          )}
        />
        <FormControl sx={{ display: 'flex', ml: 2 }}>
          <RadioGroup row name='expansionType' value={expansionType} onChange={handleChange}>
            <FormControlLabel value={expansionTypes[0]} control={<Radio />} label={t("indented")} disabled={isRawMaterial} />
            <FormControlLabel value={expansionTypes[1]} control={<Radio />} label={t("whereUsed")} />
          </RadioGroup>
        </FormControl>
        <Box sx={{ ml: "auto" }}>
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box id="item-master-grid" className="ag-theme-balham grid-area" sx={{ display: "flex", position: "relative", height: "calc(100% - 50px)", boxSizing: "border-box", flexDirection: "column" }}>
        <SplitPane split="vertical" minSize={200} defaultSize="60%" style={{ height: "100%" }} paneStyle={{ overflow: "hidden" }} >
          <Box sx={{ flex: 1, height: "95%", mr: 1.5 }}>
            <AgGridReact
              ref={itemGridRef}
              rowData={items}
              {...defaultGridMemo}
              rowSelection={{ mode: 'singleRow', checkboxes: false, headerCheckbox: false, enableClickSelection: true }}
              onGridReady={onItemGridReady}
              onSelectionChanged={onItemSelected}
              onSortChanged={params => setSortParams(GridUtils.getSortState(params))}
              statusBar={statusBar}
            />
            <Box sx={{ mt: 1 }}>
              <PaginationContainer
                currentPage={currentPage} totalPages={tPages}
                pageSize={pageSize} pageSizes={pageSizes}
                setCurrentPage={setCurrentPage} setPageSize={setPageSize}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1, height: "calc(100% - 8px)", ml: 1.5, display: "flex", flexDirection: "row" }}>
            <Box sx={{ flexGrow: 1, transition: "all 0.3s ease" }}>
              <AgGridReact
                ref={BomDetailGridRef}
                rowData={bomDetail}
                treeData
                getDataPath={(data) => data.hierarchy}
                groupDefaultExpanded={-1}
                autoGroupColumnDef={{ headerName: t("level"), field: "level", type: ["nonEditableColumn"], cellRendererParams: { suppressCount: true }, width: 150, sortable: false }}
                {...DefaultGridSetting({ title, viewName, gridId: "bomDetailGrid" })}
                getRowStyle={getRowStyle}
                rowSelection={{ mode: 'multiRow', checkboxes: false, headerCheckbox: false }}
                onGridReady={onDetailGridReady}
                onCellValueChanged={onCellValueChanged}
              />
            </Box>
            {openFilter && (
              <Box sx={{ width: '40%', maxHeight: '100%', overflowY: 'auto', ml: 1, border: 1, borderColor: "#cccccc" }}>
                <AdvancedFilter
                  columnDefs={[...itemColDefs, ...hrchyColDefs, ...attrColDefs]}
                  advancedFilters={advancedFilters}
                  onApply={setAdvancedFilters}
                />
              </Box>
            )}
          </Box>
        </SplitPane>
      </Box>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <AttributeConfigModal
        viewName={viewName}
        open={attrConfigModalOpen}
        setOpen={setAttrConfigModalOpen}
        target={"item"}
        attrFields={attrFields}
        hrchyColDefs={hrchyColDefs}
        hrchyColumnOptions={{
          type: ["defaultValueParser"],
          keyCreator: itemHrchyKeyCreator
        }}
        hrchyNumList={hrchyNumList}
        setAttrColDefs={setAttrColDefs}
        attrColumnOptions={{ valueParser: (params) => String(params.newValue) }}
        setHrchyColDefs={setHrchyColDefs}
        attrColsCollapsed={attrColsCollapsed}
      />
    </>
  );
};

export default withTranslation()(BOMDetail);
